import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { BookingCalendar } from '../BookingCalendar';
import { useGetCarBlackoutDaysHook } from '../../hooks/useGetCarBlackoutDaysHook';
import { DateTime } from 'luxon';
import {
  combinePickerDateTimeGetUTC,
  pickerDateToDayKey,
} from '../../helpers/combinePickerDateTimeGetUTC';
import { ScaleLoader } from 'react-spinners';
import { getTodayInTimezone, zoneAbbreviation } from '../../helpers/timezoneHelpers';
import {
  computeBlockedDateSets,
  disabledTimesFor,
  labelToMinutes,
  pickupLeadDisabledTimes,
  todayKey as listingTodayKey,
} from '../../helpers/bookedSlots';

const Container = styled.div`
  ${tw`w-full`}
`;

const TimezoneHint = styled.p`
  ${tw`text-sm text-text-secondary mt-2`}
`;

const Legend = styled.p`
  ${tw`text-xs text-text-secondary mt-1`}
`;

interface DateTimeRangePickerProps {
  initialStartDate?: Date;
  initialEndDate?: Date;
  initialStartTime?: string;
  initialEndTime?: string;
  startAtOptions: string[];
  endAtOptions: string[];
  onDateTimeChange: (dates: {
    startDate: Date;
    endDate: Date;
    startTime: string;
    endTime: string;
    startTimestamp: number;
    endTimestamp: number;
  }) => void;
  carId?: string;
  excludeBookingId?: string;
  startDisabled?: boolean;
  endDisabled?: boolean;
  minimumRentalDays?: number;
  /** IANA zone of the car location. Required so 1pm means 1pm there, not in the browser. */
  timezone?: string | null;
}

export const DateTimeRangePicker = ({
  initialStartDate = new Date(Date.now() + 24 * 60 * 60 * 1000),
  initialEndDate = new Date(Date.now() + 4 * 24 * 60 * 60 * 1000),
  initialStartTime = '14:00',
  initialEndTime = '14:00',
  startAtOptions,
  endAtOptions,
  onDateTimeChange,
  carId,
  excludeBookingId,
  startDisabled = false,
  endDisabled = false,
  minimumRentalDays = 1,
  timezone: timezoneProp,
}: DateTimeRangePickerProps) => {
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const [startDate, setStartDate] = useState<Date>(initialStartDate);
  const [endDate, setEndDate] = useState<Date>(initialEndDate);
  const [startTime, setStartTime] = useState(initialStartTime);
  const [endTime, setEndTime] = useState(initialEndTime);

  const { bookedSlots, tripBufferHours, timezone, loading } = useGetCarBlackoutDaysHook({
    carId,
    excludeBookingId: excludeBookingId || null,
    timezone: timezoneProp,
  });

  // Flutter parity: fully covered days (booking ± buffer) are blocked, partly covered days marked.
  const daySets = useMemo(() => {
    const today = listingTodayKey(timezone);
    const startKey = pickerDateToDayKey(startDate, timezone);
    const fromKey = startKey < today ? startKey : today;
    const toKey = DateTime.fromISO(today).plus({ years: 1 }).toISODate() || today;
    return computeBlockedDateSets({
      slots: bookedSlots,
      bufferHours: tripBufferHours,
      fromKey,
      toKey,
      timezone,
    });
  }, [bookedSlots, tripBufferHours, timezone, pickerDateToDayKey(startDate, timezone)]);

  // Chips inside a booking (± buffer) or before the 90-minute pickup lead are removed.
  const filterTimes = (options: string[], date: Date, isStart: boolean) => {
    const dayKey = pickerDateToDayKey(date, timezone);
    const disabled = disabledTimesFor({
      dayKey,
      slots: bookedSlots,
      bufferHours: tripBufferHours,
      timezone,
    });
    if (isStart) {
      pickupLeadDisabledTimes({ dayKey, timezone }).forEach(m => disabled.add(m));
    }
    const kept = options.filter(option => {
      const minute = labelToMinutes(option);
      return Number.isNaN(minute) || !disabled.has(minute);
    });
    return kept.length > 0 ? kept : options;
  };
  const startTimes = useMemo(
    () => filterTimes(startAtOptions, startDate, true),
    [startAtOptions, startDate, bookedSlots, tripBufferHours, timezone],
  );
  const endTimes = useMemo(
    () => filterTimes(endAtOptions, endDate, false),
    [endAtOptions, endDate, bookedSlots, tripBufferHours, timezone],
  );

  useEffect(() => {
    if (startTimes.length > 0 && !startTimes.includes(startTime)) setStartTime(startTimes[0]);
  }, [startTimes, startTime]);
  useEffect(() => {
    if (endTimes.length > 0 && !endTimes.includes(endTime)) setEndTime(endTimes[0]);
  }, [endTimes, endTime]);

  // Sync internal state when initial props change
  useEffect(() => {
    if (initialStartDate) {
      const initialTime = initialStartDate.getTime();
      const currentTime = startDate.getTime();
      // Only update if dates are different (allow some tolerance for time component differences)
      const initialDateOnly = new Date(
        initialStartDate.getFullYear(),
        initialStartDate.getMonth(),
        initialStartDate.getDate(),
      ).getTime();
      const currentDateOnly = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate(),
      ).getTime();
      if (initialDateOnly !== currentDateOnly) {
        setStartDate(initialStartDate);
      }
    }
  }, [initialStartDate]);

  useEffect(() => {
    if (initialEndDate) {
      const initialDateOnly = new Date(
        initialEndDate.getFullYear(),
        initialEndDate.getMonth(),
        initialEndDate.getDate(),
      ).getTime();
      const currentDateOnly = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate(),
      ).getTime();

      // Calculate minimum end date based on minimumRentalDays
      // minimumRentalDays <= 1: same day is OK (duration-based, not calendar-based)
      // minimumRentalDays > 1: need at least (minimumRentalDays - 1) calendar days after start
      const daysToAdd = minimumRentalDays > 1 ? minimumRentalDays - 1 : 0;
      const minimumEndDate = new Date(startDate);
      minimumEndDate.setDate(minimumEndDate.getDate() + daysToAdd);
      const minimumDateOnly = new Date(
        minimumEndDate.getFullYear(),
        minimumEndDate.getMonth(),
        minimumEndDate.getDate(),
      ).getTime();

      // Sync if dates are different AND meets minimum rental requirement
      if (initialDateOnly !== currentDateOnly && initialDateOnly >= minimumDateOnly) {
        setEndDate(initialEndDate);
      }
    }
  }, [initialEndDate, startDate, minimumRentalDays]);

  useEffect(() => {
    if (initialStartTime && initialStartTime !== startTime) {
      setStartTime(initialStartTime);
    }
  }, [initialStartTime]);

  useEffect(() => {
    if (initialEndTime && initialEndTime !== endTime) {
      setEndTime(initialEndTime);
    }
  }, [initialEndTime]);

  // Auto-adjust endDate based on minimumRentalDays
  // minimumRentalDays <= 1: same-day bookings allowed (1 min to 24 hours = 1 day rental)
  // minimumRentalDays = 2: end date must be at least 1 day after start (24-48 hours = 2 days)
  // minimumRentalDays = 5: end date must be at least 4 days after start (96-120 hours = 5 days)
  useEffect(() => {
    const endDateOnly = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
    ).getTime();

    // Calculate minimum end date based on minimumRentalDays
    // For minimumRentalDays <= 1, same day is allowed (daysToAdd = 0)
    // For minimumRentalDays = 2, need at least next day (daysToAdd = 1)
    // For minimumRentalDays = 5, need at least 4 days later (daysToAdd = 4)
    const daysToAdd = minimumRentalDays > 1 ? minimumRentalDays - 1 : 0;
    const minimumEndDate = new Date(startDate);
    minimumEndDate.setDate(minimumEndDate.getDate() + daysToAdd);
    const minimumEndDateOnly = new Date(
      minimumEndDate.getFullYear(),
      minimumEndDate.getMonth(),
      minimumEndDate.getDate(),
    ).getTime();

    if (endDateOnly < minimumEndDateOnly) {
      setEndDate(minimumEndDate);
    }
  }, [startDate, minimumRentalDays]);

  useEffect(() => {
    if (!timezone) return;

    const startTimestamp = combinePickerDateTimeGetUTC(startDate, startTime, timezone);
    const endTimestamp = combinePickerDateTimeGetUTC(endDate, endTime, timezone);

    onDateTimeChange({
      startDate,
      endDate,
      startTime,
      endTime,
      startTimestamp,
      endTimestamp,
    });
  }, [startDate, endDate, startTime, endTime, timezone]);

  const onStartDateChange = (date: Date) => {
    setStartDate(date);
    setShowStartCalendar(false);
  };

  const onEndDateChange = (date: Date) => {
    setEndDate(date);
    setShowEndCalendar(false);
  };

  if (loading) {
    return <ScaleLoader color="#fff" height={30} width={10} radius={5} margin={5} />;
  }

  return (
    <Container>
      <BookingCalendar
        selectedDate={startDate}
        showCalendar={showStartCalendar}
        timeOptions={startTimes}
        selectedTime={startTime}
        onDateChange={onStartDateChange}
        onTimeChange={time => setStartTime(time)}
        onToggleCalendar={() => setShowStartCalendar(!showStartCalendar)}
        minDate={getTodayInTimezone(timezone)}
        blocked={daySets.blocked}
        partial={daySets.partial}
        timezone={timezone}
        disabled={startDisabled}
      />
      <BookingCalendar
        selectedDate={endDate}
        showCalendar={showEndCalendar}
        timeOptions={endTimes}
        selectedTime={endTime}
        onDateChange={onEndDateChange}
        onTimeChange={time => setEndTime(time)}
        onToggleCalendar={() => setShowEndCalendar(!showEndCalendar)}
        minDate={startDate}
        blocked={daySets.blocked}
        partial={daySets.partial}
        timezone={timezone}
        disabled={endDisabled}
      />
      {(daySets.blocked.size > 0 || daySets.partial.size > 0) && (
        <Legend>Struck-through days are booked; a dot means part of the day is taken.</Legend>
      )}
      {timezone ? (
        <TimezoneHint>
          Times are in the vehicle&apos;s local timezone ({zoneAbbreviation(timezone)})
        </TimezoneHint>
      ) : null}
    </Container>
  );
};
